import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useRouterActions } from 'react-resource-router';

import Button from '@atlaskit/button';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import IconRemove from '@atlaskit/icon/glyph/editor/remove';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import InlineDialog from '@atlaskit/inline-dialog';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import TableTree, {
  Cell,
  Header,
  Headers,
  Row,
  Rows,
} from '@atlaskit/table-tree';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { Repository, Schedule } from 'src/components/pipelines/models';
import { ScheduleVariable } from 'src/components/pipelines/models/Schedule';
import { removePipelinesData } from 'src/components/pipelines/redux/actions/pipelines';
import { buildFilterPathParam } from 'src/components/pipelines/utils/pipeline-filter-query';
import { LoadingStatus } from 'src/constants/loading-status';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useParams } from 'src/router/hooks';

import {
  DOCS_SCHEDULED_PIPELINES,
  INCREASED_MAX_SCHEDULED_PIPELINES,
  MAX_SCHEDULED_PIPELINES,
} from '../const';
import { humanise } from '../utils/parseSchedule';

import CreateScheduleModal from './CreateScheduleModal';
import {
  CreateScheduleButtonOverlay,
  CreateScheduleButtonWrapper,
  DeleteBtnCell,
  EmptySchedules,
  OpenDeleteScheduleDialogButton,
  SchedulesDateWrapper,
  SchedulesDialogWrapper,
  SchedulesRows,
  SchedulesTitle,
  SchedulesWarning,
  SchedulesWrapper,
  columnHeaderStyles,
  VariableCell,
  containerStyles,
} from './styled';

export type Props = {
  createSchedule: (props: any) => void;
  deleteSchedule: (schedule: Schedule) => void;
  disableSchedule: (schedule: Schedule) => void;
  enableSchedule: (schedule: Schedule) => void;
  fetchBranches: (props: any) => any;
  fetchingSchedules: boolean;
  getSchedules: () => void;
  repository: Repository;
  schedules: Schedule[];
  _isDialogOpen?: boolean;
  scheduleStatus: LoadingStatus;
};

const Schedules = ({
  createSchedule,
  deleteSchedule,
  disableSchedule,
  enableSchedule,
  fetchBranches,
  fetchingSchedules,
  getSchedules,
  repository,
  schedules,
  _isDialogOpen,
  scheduleStatus,
}: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(_isDialogOpen);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<
    false | Schedule
  >(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [cronErrorMessage, setCronErrorMessage] = useState<string>('');
  const dispatch = useDispatch();
  const { push } = useRouterActions();
  const params = useParams<{
    repositoryFullSlug: string;
    branch: string;
    page: string;
  }>();
  const { repositoryFullSlug } = params;
  const isScheduleModalFilterLinkEnabled = useStatsigGate(
    StatsigFeatureKeys.isScheduleModalFilterLinkEnabled
  ) as boolean;

  useEffect(() => {
    if (isDialogOpen) {
      getSchedules();
    }
  }, [getSchedules, isDialogOpen]);

  const toggleShowingDialog = useCallback(() => {
    setIsDialogOpen(!isDialogOpen);
  }, [setIsDialogOpen, isDialogOpen]);

  const onCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, [setIsDialogOpen]);

  const onCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, [setIsDeleteDialogOpen]);

  const onOpenDeleteDialog = useCallback(
    (schedule: any) => {
      setIsDeleteDialogOpen(schedule);
    },
    [setIsDeleteDialogOpen]
  );

  const onConfirmDeleteDialog = useCallback(
    (schedule: any) => {
      deleteSchedule(schedule);
      onCloseDeleteDialog();
    },
    [deleteSchedule, onCloseDeleteDialog]
  );

  const onOpenCreateDialog = useCallback(
    (_schedule: any) => {
      setIsCreateDialogOpen(true);
    },
    [setIsCreateDialogOpen]
  );

  const onCloseCreateDialog = useCallback(
    (schedule: any) => {
      if (!(schedule?.nativeEvent instanceof Event)) {
        createSchedule(schedule);
      }
    },
    [createSchedule]
  );
  const onCancelDialog = useCallback(() => {
    setIsCreateDialogOpen(false);
    setCronErrorMessage('');
  }, [setIsCreateDialogOpen]);

  useEffect(() => {
    if (scheduleStatus === LoadingStatus.Success) {
      setIsCreateDialogOpen(false);
      setCronErrorMessage('');
    } else if (scheduleStatus === LoadingStatus.Failed) {
      setCronErrorMessage('Please enter a valid cron expression');
    }
  }, [scheduleStatus, setIsCreateDialogOpen, setCronErrorMessage]);

  const isIncreasedMaxRepoSchedulesEnabled = useStatsigGate(
    StatsigFeatureKeys.isIncreasedMaxRepoSchedulesEnabled
  );

  const SCHEDULED_PIPELINES_LIMIT = isIncreasedMaxRepoSchedulesEnabled
    ? INCREASED_MAX_SCHEDULED_PIPELINES
    : MAX_SCHEDULED_PIPELINES;

  const isCreateDisabled = useMemo(() => {
    return (
      (schedules && schedules.length >= SCHEDULED_PIPELINES_LIMIT) ||
      !repository.canWrite
    );
  }, [schedules, repository, SCHEDULED_PIPELINES_LIMIT]);

  const tooltipContent = useMemo(() => {
    const contentToRender = isCreateDisabled && !fetchingSchedules;

    if (contentToRender && !repository.canWrite) {
      return 'To create scheduled pipelines you need write permission.';
    } else if (contentToRender) {
      return `Maximum ${SCHEDULED_PIPELINES_LIMIT} scheduled pipelines are allowed.`;
    } else {
      return '';
    }
  }, [
    repository,
    isCreateDisabled,
    fetchingSchedules,
    SCHEDULED_PIPELINES_LIMIT,
  ]);

  const onToggleSchedule = useCallback(
    (schedule: Schedule) => {
      if (schedule.enabled) {
        disableSchedule(schedule);
      } else {
        enableSchedule(schedule);
      }
    },
    [disableSchedule, enableSchedule]
  );
  const generateFilterUrl = useCallback(
    (schedule: any) => {
      const pattern = schedule['target.selector.pattern'];
      const type = schedule['target.selector.type'];
      const filterPath = buildFilterPathParam({
        selectedTriggerType: 'Scheduled',
        selectedPipelineType: pattern ? `${type}: ${pattern}` : type,
      });
      const filterBranch = schedule['target.ref_name'];
      return `/${repositoryFullSlug}/pipelines/results/branch/${encodeURIComponent(
        filterBranch
      )}/page/1${filterPath}`;
    },
    [repositoryFullSlug]
  );

  const onUpdateFilter = useCallback(
    (schedule: any) => {
      const url = generateFilterUrl(schedule);
      dispatch(removePipelinesData());
      push(url);
    },
    [push, generateFilterUrl, dispatch]
  );
  const renderVariableHeader = () => (
    <Row>
      <Cell width={65} />
      <VariableCell width={150}>
        <strong>Variable Name</strong>
      </VariableCell>
      <VariableCell width={150}>
        <strong>Variable Value</strong>
      </VariableCell>
    </Row>
  );
  const renderVariableRow = (item: ScheduleVariable) => (
    <Row>
      <Cell width={65} />
      <VariableCell width={150}>
        <Tooltip position="bottom" content={item.key}>
          <div>{item.key}</div>
        </Tooltip>
      </VariableCell>
      <VariableCell width={150}>
        <Tooltip position="bottom" content={item.value}>
          <div>{item.value}</div>
        </Tooltip>
      </VariableCell>
    </Row>
  );
  const renderScheduleRow = useCallback(
    (
      item: Schedule,
      scheduleName: (p: { withTypeLabel: boolean }) => string
    ) => (
      <Row
        itemId={item.uuid}
        items={item.variables}
        hasChildren={item?.variables?.length > 0}
        className="schedule-row"
      >
        <Cell width={65}>
          <Toggle
            isDisabled={!item.uuid}
            id={`toggle-schedule-${item.uuid}`}
            label="Enable Schedule"
            isChecked={item.enabled}
            onChange={() => onToggleSchedule(item)}
          />
        </Cell>
        <Cell width={85}>
          <div title={item['target.ref_name']}>{item['target.ref_name']}</div>
        </Cell>
        <Cell width={110}>
          <Tooltip
            position="bottom"
            content={scheduleName({ withTypeLabel: true })}
          >
            {isScheduleModalFilterLinkEnabled ? (
              <Button
                appearance="link"
                onClick={() => onUpdateFilter(item)}
                spacing="none"
              >
                {scheduleName({ withTypeLabel: false })}
              </Button>
            ) : (
              <div>{scheduleName({ withTypeLabel: false })}</div>
            )}
          </Tooltip>
        </Cell>
        <Cell width={138}>
          {item.cron_pattern && (
            <SchedulesDateWrapper>
              <div title={humanise(item.cron_pattern)}>
                {humanise(item.cron_pattern)}
              </div>
              {item.executions[0]?.['error.message'] && (
                <SchedulesWarning>
                  <Tooltip content={item.executions[0]['error.message']}>
                    <WarningIcon
                      label={item.executions[0]['error.message']}
                      size="small"
                    />
                  </Tooltip>
                </SchedulesWarning>
              )}
            </SchedulesDateWrapper>
          )}
        </Cell>
        <DeleteBtnCell width={40}>
          {repository.canWrite && item.uuid ? (
            <OpenDeleteScheduleDialogButton
              appearance="subtle"
              iconAfter={<IconRemove label="Remove" />}
              onClick={() => onOpenDeleteDialog(item)}
            />
          ) : (
            <Spinner size="small" />
          )}
        </DeleteBtnCell>
      </Row>
    ),
    [
      onToggleSchedule,
      onUpdateFilter,
      onOpenDeleteDialog,
      repository,
      isScheduleModalFilterLinkEnabled,
    ]
  );
  const renderRow = useCallback(
    (item: Schedule | ScheduleVariable, parentSchedule?: Schedule) => {
      if ('key' in item) {
        const shouldRenderChildHeader =
          parentSchedule?.variables?.[0]?.uuid === item.uuid;
        return (
          <>
            {shouldRenderChildHeader && renderVariableHeader()}
            {renderVariableRow(item)}
          </>
        );
      } else {
        const scheduleName = (p = { withTypeLabel: false }) => {
          const typeLabel =
            item['target.selector.type'] &&
            (p.withTypeLabel || !item['target.selector.pattern'])
              ? item['target.selector.type']
              : '';
          return `${typeLabel}${
            item['target.selector.pattern']
              ? (typeLabel ? ': ' : '') + item['target.selector.pattern']
              : ''
          }`;
        };
        return renderScheduleRow(item, scheduleName);
      }
    },
    [renderScheduleRow]
  );
  return (
    <>
      <InlineDialog
        content={
          <SchedulesDialogWrapper>
            <SchedulesWrapper>
              <header>
                <SchedulesTitle>Scheduled pipelines</SchedulesTitle>
                <Tooltip position="bottom" content={tooltipContent}>
                  <CreateScheduleButtonWrapper>
                    {(fetchingSchedules || isCreateDisabled) && (
                      <CreateScheduleButtonOverlay />
                    )}
                    <Button
                      isDisabled={fetchingSchedules || isCreateDisabled}
                      onClick={onOpenCreateDialog}
                    >
                      New schedule
                    </Button>
                  </CreateScheduleButtonWrapper>
                </Tooltip>
              </header>
              {fetchingSchedules ? (
                <Spinner size="small" />
              ) : (
                <Box xcss={containerStyles}>
                  <TableTree>
                    {schedules.length > 0 ? (
                      <>
                        <Headers xcss={columnHeaderStyles}>
                          <Header width={70} />
                          <Header width={80}>Branch</Header>
                          <Header width={110}>Pipeline</Header>
                          <Header width={138}>Schedule</Header>
                          <Header width={40} />
                        </Headers>
                        <SchedulesRows>
                          <Rows
                            items={schedules}
                            render={item =>
                              renderRow(
                                item,
                                schedules.find(parentSchedule =>
                                  parentSchedule.variables?.some(
                                    child => child.uuid === item.uuid
                                  )
                                )
                              )
                            }
                          />
                        </SchedulesRows>
                      </>
                    ) : (
                      <EmptySchedules>
                        <CalendarIcon
                          label="schedule"
                          size="xlarge"
                          primaryColor={token('color.text.subtlest')}
                        />
                        <p>
                          You haven&apos;t configured any schedules yet.{' '}
                          <Button
                            href={DOCS_SCHEDULED_PIPELINES}
                            spacing="none"
                            appearance="link"
                            target="_blank"
                          >
                            Learn more
                          </Button>
                        </p>
                      </EmptySchedules>
                    )}
                  </TableTree>
                </Box>
              )}
            </SchedulesWrapper>
          </SchedulesDialogWrapper>
        }
        onClose={onCloseDialog}
        isOpen={isDialogOpen}
        placement="bottom-end"
      >
        <Button onClick={toggleShowingDialog}>Schedules</Button>
      </InlineDialog>
      <ModalTransition>
        {isCreateDialogOpen !== false ? (
          <CreateScheduleModal
            fetchBranches={fetchBranches}
            onCloseDialog={onCloseCreateDialog}
            onCancelDialog={onCancelDialog}
            cronErrorMessage={cronErrorMessage}
            scheduleStatus={scheduleStatus}
            showBlanket
          />
        ) : null}
        {isDeleteDialogOpen !== false ? (
          <Modal onClose={onCloseDeleteDialog}>
            <ModalHeader>
              <ModalTitle appearance="danger">Delete schedule</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to delete this schedule?</p>
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={onCloseDeleteDialog}>
                Cancel
              </Button>
              <Button
                appearance="danger"
                onClick={() => onConfirmDeleteDialog(isDeleteDialogOpen)}
                autoFocus
              >
                Delete
              </Button>
            </ModalFooter>
          </Modal>
        ) : null}
      </ModalTransition>
    </>
  );
};

export default memo(Schedules);
